import React, { useState, useEffect, useCallback } from 'react';
import { postService, channelService } from '../api/services';

const SORT_ORDER_LABEL = { newest: '最新發表', popular: '最熱門', answered: '已解決優先', custom: '自定義順序' };

// 後台「文章排序」分頁：選定頻道後列出該頻道所有文章，可調整 custom_rank（數字越大排序越前面）。
// 僅在該頻道的「文章排序」模式設為『自定義順序』時，這裡調整的數值才會反映在前台列表。
const AdminPostOrderPanel = () => {
  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState('');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    channelService.listAll()
      .then((res) => {
        setChannels(res.data || []);
        if ((res.data || []).length) setChannelId((prev) => prev || res.data[0].id);
      })
      .catch((err) => setError(err.response?.data?.error || err.message));
  }, []);

  const loadPosts = useCallback(() => {
    if (!channelId) return;
    setLoading(true);
    postService.list({ channel: channelId, sort: 'custom', limit: 100 })
      .then((res) => setPosts(res.data || []))
      .catch((err) => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, [channelId]);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const currentChannel = channels.find((c) => c.id === channelId);

  // 僅更新畫面上的暫存值，離開輸入框（onBlur）才真正送出，避免每個按鍵都打一次 API
  const handleRankInput = (postId, value) => {
    setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, custom_rank: value } : p)));
  };

  const handleRankSave = async (post) => {
    const rank = Number.parseInt(post.custom_rank, 10) || 0;
    setSavingId(post.id);
    try {
      await postService.updateRank(post.id, rank);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSavingId(null);
    }
  };

  // 快速將頻道的文章排序模式切換為「自定義順序」：沿用完整更新 API，其餘欄位取自列表既有資料
  const handleEnableCustomSort = async () => {
    if (!currentChannel) return;
    try {
      await channelService.update(currentChannel.id, {
        name: currentChannel.name,
        slug: currentChannel.slug,
        description: currentChannel.description,
        icon: currentChannel.icon,
        sort_order: currentChannel.sort_order,
        required_fields: (currentChannel.required_fields || '').includes('tags') ? ['tags'] : [],
        allow_poll: (currentChannel.features || '').includes('poll'),
        status: currentChannel.status,
        post_sort_order: 'custom',
      });
      const res = await channelService.listAll();
      setChannels(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  return (
    <div className="admin-post-order-panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>文章排序</h2>
        <select className="form-control form-control-sm" style={{ width: 220 }} value={channelId} onChange={(e) => setChannelId(e.target.value)}>
          {channels.map((c) => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
        </select>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {currentChannel && currentChannel.post_sort_order !== 'custom' && (
        <div className="alert alert-warning" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span>此頻道的文章排序模式目前為「{SORT_ORDER_LABEL[currentChannel.post_sort_order] || currentChannel.post_sort_order}」，下面調整的數值不會反映在前台，需先切換為「自定義順序」。</span>
          <button className="btn btn-sm btn-primary" onClick={handleEnableCustomSort}>立即切換為自定義順序</button>
        </div>
      )}

      <p style={{ color: '#888', fontSize: 13 }}>數字越大排序越前面；未設定（維持 0）的文章之間依發文時間新到舊排列。同頻道最多顯示前 100 篇。</p>

      {loading ? (
        <div className="reward-empty">載入中...</div>
      ) : posts.length === 0 ? (
        <div className="reward-empty">此頻道尚無文章</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="admin-table">
            <thead>
              <tr><th>標題</th><th>作者</th><th>發文時間</th><th>排序數值</th><th></th></tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.id}>
                  <td>{p.is_pinned ? '📌 ' : ''}{p.title}</td>
                  <td>{p.author_name}</td>
                  <td>{p.created_at}</td>
                  <td>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      style={{ width: 90 }}
                      value={p.custom_rank}
                      onChange={(e) => handleRankInput(p.id, e.target.value)}
                      onBlur={() => handleRankSave(p)}
                    />
                  </td>
                  <td>{savingId === p.id ? '儲存中...' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminPostOrderPanel;
